import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface EditorialItem {
  id?: number;
  title: string;
  excerpt?: string;
  slug: string;
  featured_image?: string;
}

interface EditorialTeaserProps {
  items: EditorialItem[];
}

const Section = styled.section`
  padding: clamp(70px, 11vw, 120px) 0;
  background: linear-gradient(180deg, #ffffff 0%, #fdfaf6 80%, #f7f3ee 100%);
`;

const Inner = styled.div`
  display: grid;
  gap: clamp(32px, 6vw, 48px);
`;

const Header = styled.div`
  display: grid;
  gap: 12px;
  max-width: 640px;
`;

const Title = styled.h2`
  font-size: clamp(1.95rem, 3vw, 2.6rem);
  font-weight: 600;
  color: #2c2a29;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(44, 42, 41, 0.7);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(20px, 5vw, 32px);
`;

const Card = styled(Link)`
  display: grid;
  gap: 16px;
  padding: 28px;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(47, 58, 52, 0.08);
  box-shadow: 0 18px 30px rgba(37, 45, 40, 0.1);
  color: #2c2a29;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 24px 44px rgba(37, 45, 40, 0.18);
  }
`;

const CardImage = styled.div<{ image?: string }>`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 3 / 2;
  background: ${({ image }) =>
    image
      ? `linear-gradient(180deg, rgba(24, 28, 25, 0.15), rgba(24, 28, 25, 0.4)), url('${image}') center/cover`
      : 'linear-gradient(160deg, rgba(47, 58, 52, 0.8), rgba(145, 120, 97, 0.8))'};
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
`;

const CardExcerpt = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(44, 42, 41, 0.65);
  margin: 0;
`;

const CardMeta = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(47, 58, 52, 0.5);
`;

const EmptyState = styled.div`
  padding: 32px;
  border-radius: 24px;
  background: rgba(247, 243, 238, 0.8);
  border: 1px dashed rgba(47, 58, 52, 0.3);
  color: rgba(44, 42, 41, 0.6);
  text-align: center;
`;

const EditorialTeaser: React.FC<EditorialTeaserProps> = ({ items }) => {
  const hasItems = items && items.length > 0;

  return (
    <Section>
      <div className="container">
        <Inner>
          <Header>
            <Title>Floristens tips & inspiration</Title>
            <Subtitle>
              Fördjupa dig i vår värld av dofter, dukningar och blomsterarrangemang. Låt dig inspireras av
              guider och säsongens kreativa idéer.
            </Subtitle>
          </Header>

          {hasItems ? (
            <Grid>
              {items.map(item => (
                <Card key={item.slug} to={`/${item.slug}`}>
                  <CardImage image={item.featured_image} />
                  <CardMeta>Inspiration</CardMeta>
                  <CardTitle>{item.title}</CardTitle>
                  {item.excerpt && <CardExcerpt>{item.excerpt}</CardExcerpt>}
                </Card>
              ))}
            </Grid>
          ) : (
            <EmptyState>
              Vi uppdaterar just nu våra inspirationsartiklar. Under tiden är du varmt välkommen att kontakta oss
              för tips och råd kring buketter, presenter och styling.
            </EmptyState>
          )}
        </Inner>
      </div>
    </Section>
  );
};

export type { EditorialItem };
export default EditorialTeaser;
